import { useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Guard } from '@jywer/ui';
import type { LeadSource, RegisterResult } from '@jywer/intake';
import { CreateOrderProductRow } from './CreateOrderProductRow';
import {
  buildOrderDraft,
  emptyOrderForm,
  emptyProduct,
  type OrderFormState,
  type OrderShare,
  type ProductFormState,
} from './intake-form-state';
import { orderDraftSchema } from './intake-schema';
import {
  fetchAllSalesReps,
  fetchClientsList,
  fetchMe,
  registerOrder,
  type ClientOption,
  type IntakeRegisterError,
  type SalesRepOption,
} from './intake-api';
import { initContractUpload, uploadContract } from './api';

const INPUT_CLS =
  'rounded border border-line-2 bg-paper-0 px-2 py-1 text-sm focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';

const LEAD_SOURCE_LABEL: Record<LeadSource, string> = {
  cold_new_client: 'Cold — ny kunde',
  inbound_new_client: 'Inbound — ny kunde',
  existing_client: 'Eksisterende kunde',
};

interface FieldErrors {
  [path: string]: string;
}

function flattenErrors(issues: ReturnType<typeof orderDraftSchema.safeParse>): FieldErrors {
  const out: FieldErrors = {};
  if ('error' in issues && issues.error) {
    for (const issue of issues.error.issues) {
      const key = issue.path.join('.');
      if (!out[key]) out[key] = issue.message;
    }
  }
  return out;
}

function CreateOrderForm() {
  const navigate = useNavigate();
  const [form, setForm] = useState<OrderFormState>(() => emptyOrderForm());
  const [reps, setReps] = useState<SalesRepOption[]>([]);
  const [clients, setClients] = useState<ClientOption[]>([]);
  const [clientQuery, setClientQuery] = useState('');
  const [pickedClient, setPickedClient] = useState<ClientOption | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FieldErrors>({});
  const [serverError, setServerError] = useState<IntakeRegisterError | null>(null);
  const [success, setSuccess] = useState<RegisterResult | null>(null);
  // PR #7 — contract upload state. The R2 key is owned by the form; the
  // file input ref is local UX. Upload errors surface inline rather
  // than blocking the rest of the form.
  const contractFileRef = useRef<HTMLInputElement>(null);
  const [uploadingContract, setUploadingContract] = useState(false);
  const [contractUploadError, setContractUploadError] = useState<string | null>(null);

  // Bootstrap pickers + default sales rep to current user.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const [me, repsData, clientsData] = await Promise.all([
        fetchMe().catch(() => null),
        fetchAllSalesReps().catch(() => []),
        fetchClientsList().catch(() => []),
      ]);
      if (cancelled) return;
      setReps(repsData);
      setClients(clientsData);
      if (me?.userId) {
        setForm((prev) => (prev.salesRepId ? prev : { ...prev, salesRepId: me.userId }));
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const filteredClients = useMemo(() => {
    const q = clientQuery.trim().toLowerCase();
    if (!q) return clients.slice(0, 30);
    return clients
      .filter(
        (c) =>
          c.name.toLowerCase().includes(q) ||
          (c.invoice_email ?? '').toLowerCase().includes(q) ||
          (c.vat_number ?? '').toLowerCase().includes(q),
      )
      .slice(0, 30);
  }, [clients, clientQuery]);

  function update<K extends keyof OrderFormState>(key: K, value: OrderFormState[K]) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function setProduct(i: number, next: ProductFormState) {
    setForm((prev) => {
      const arr = [...prev.products];
      arr[i] = next;
      return { ...prev, products: arr };
    });
  }

  function addProduct() {
    setForm((prev) => ({ ...prev, products: [...prev.products, emptyProduct('website')] }));
  }

  function removeProduct(i: number) {
    setForm((prev) => ({
      ...prev,
      products: prev.products.filter((_, idx) => idx !== i),
    }));
  }

  // PR #7 — contract upload. Uploads to R2 immediately on file pick so
  // the r2_key is ready by the time the user submits the order. The
  // /kunde Kontrakter view reads from `orders.signed_contract_r2_key`
  // already; capturing at intake closes the loop on decision #9.
  async function onContractFileChosen(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    setContractUploadError(null);
    setUploadingContract(true);
    try {
      const init = await initContractUpload(file.name, file.type);
      await uploadContract(init.upload_url, file);
      setForm((prev) => ({
        ...prev,
        signedContractR2Key: init.r2_key,
        signedContractFilename: file.name,
      }));
    } catch (ex) {
      setContractUploadError(ex instanceof Error ? ex.message : 'Upload fejlede');
    } finally {
      setUploadingContract(false);
      if (contractFileRef.current) contractFileRef.current.value = '';
    }
  }

  function clearContract() {
    setForm((prev) => ({ ...prev, signedContractR2Key: null, signedContractFilename: null }));
    setContractUploadError(null);
  }

  async function submit() {
    setErrors({});
    setServerError(null);
    setSuccess(null);

    const draft = buildOrderDraft(form);
    const parsed = orderDraftSchema.safeParse(draft);
    if (!parsed.success) {
      setErrors(flattenErrors(parsed));
      return;
    }
    // Belt-and-braces UX warnings beyond zod.
    if (
      !draft.client_vat &&
      draft.is_new_client_explicit !== true &&
      draft.lead_source !== 'cold_new_client' &&
      draft.lead_source !== 'inbound_new_client'
    ) {
      setErrors({
        client_vat:
          'CVR mangler. Sæt "Ny kunde uden CVR" hvis dette er en helt ny kunde uden CVR endnu.',
      });
      return;
    }

    setSubmitting(true);
    try {
      const res = await registerOrder(draft);
      if (res.ok) {
        setSuccess(res.result);
      } else {
        setServerError(res.error);
        if (res.error.details && Array.isArray(res.error.details)) {
          // zod issues from server
          const fieldErrors: FieldErrors = {};
          for (const d of res.error.details as Array<{ path?: unknown; message?: string }>) {
            const path = Array.isArray(d.path) ? d.path.join('.') : '';
            if (path && d.message) fieldErrors[path] = d.message;
          }
          if (Object.keys(fieldErrors).length > 0) setErrors(fieldErrors);
        }
      }
    } finally {
      setSubmitting(false);
    }
  }

  if (success) {
    return (
      <div className="flex flex-col gap-4">
        <div className="rounded border border-status-ok/40 bg-status-ok/5 px-4 py-4">
          <h2 className="text-lg font-semibold text-status-ok">Ordre oprettet</h2>
          <p className="mt-1 text-sm text-ink-1">
            Order id: <code className="rounded bg-paper-1 px-1.5 py-0.5">{success.order_id}</code>
          </p>
          {success.project_ids.length > 0 ? (
            <div className="mt-2 text-sm text-ink-1">
              <span className="text-ink-2">Oprettede projekter:</span>{' '}
              {success.project_ids.join(', ')}
            </div>
          ) : null}
          {success.subscription_ids.length > 0 ? (
            <div className="mt-1 text-sm text-ink-1">
              <span className="text-ink-2">Oprettede abonnementer:</span>{' '}
              {success.subscription_ids.join(', ')}
            </div>
          ) : null}
        </div>
        <div className="flex gap-2">
          <button
            type="button"
            onClick={() => navigate('/orders')}
            className="rounded border border-line-2 px-3 py-1.5 text-sm text-primary"
          >
            ← Tilbage til ordrer
          </button>
          <button
            type="button"
            onClick={() => {
              setSuccess(null);
              setForm(emptyOrderForm(form.salesRepId));
              setPickedClient(null);
              setClientQuery('');
            }}
            className="rounded bg-primary px-3 py-1.5 text-sm text-white"
          >
            Opret en ny ordre
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div>
        <div className="text-xs uppercase tracking-wide text-ink-2">
          <Link to="/orders" className="hover:underline">
            Ordrer
          </Link>{' '}
          · Ny ordre
        </div>
        <h1 className="mt-1 text-2xl font-semibold text-primary">Ny ordre</h1>
        <p className="text-sm text-ink-2">
          Manuel registrering. Kalder samme <code>/api/intake/register_order</code>-endpoint som
          sales-rep automation. Server validerer altid felterne igen.
        </p>
      </div>

      {serverError ? (
        <div className="rounded border border-status-err/40 bg-status-err/5 px-4 py-3 text-sm text-status-err">
          <strong>{serverError.code}:</strong> {serverError.message}
        </div>
      ) : null}

      <section className="rounded border border-line-1 bg-paper-0 p-4">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-2">Kunde</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
          <div className="md:col-span-2">
            <label className="block text-xs text-ink-2">Kundenavn</label>
            {pickedClient ? (
              <div className="mt-1 flex items-center gap-2 rounded border border-line-1 bg-paper-1 px-2 py-1">
                <span className="flex-1 text-sm">{pickedClient.name}</span>
                <button
                  type="button"
                  onClick={() => {
                    setPickedClient(null);
                    update('clientName', '');
                    setClientQuery('');
                  }}
                  className="text-xs text-ink-2 underline"
                >
                  Skift
                </button>
              </div>
            ) : (
              <>
                <input
                  type="text"
                  value={form.clientName}
                  onChange={(e) => {
                    update('clientName', e.target.value);
                    setClientQuery(e.target.value);
                  }}
                  placeholder="Søg eksisterende kunde eller skriv nyt navn"
                  className={`${INPUT_CLS} w-full`}
                />
                {clientQuery.trim() && filteredClients.length > 0 ? (
                  <div className="mt-1 max-h-40 overflow-y-auto rounded border border-line-1 bg-paper-1">
                    {filteredClients.map((c) => (
                      <button
                        key={c.id}
                        type="button"
                        onClick={() => {
                          setPickedClient(c);
                          update('clientName', c.name);
                          if (c.invoice_email) update('invoiceEmail', c.invoice_email);
                          if (c.vat_number) update('clientVat', c.vat_number);
                          setClientQuery('');
                        }}
                        className="block w-full px-2 py-1 text-left text-xs hover:bg-paper-2"
                      >
                        {c.name}
                        {c.invoice_email ? (
                          <span className="ml-1 text-ink-3">({c.invoice_email})</span>
                        ) : null}
                      </button>
                    ))}
                  </div>
                ) : null}
              </>
            )}
            {errors['client_name'] ? (
              <p className="mt-0.5 text-xs text-status-err">{errors['client_name']}</p>
            ) : null}
          </div>

          <label className="flex flex-col gap-1 text-xs text-ink-2">
            Faktura-email
            <input
              type="email"
              value={form.invoiceEmail}
              onChange={(e) => update('invoiceEmail', e.target.value)}
              className={INPUT_CLS}
            />
            {errors['invoice_email'] ? (
              <span className="text-status-err">{errors['invoice_email']}</span>
            ) : null}
          </label>

          <label className="flex flex-col gap-1 text-xs text-ink-2">
            CVR (valgfri)
            <input
              type="text"
              value={form.clientVat}
              onChange={(e) => update('clientVat', e.target.value)}
              className={INPUT_CLS}
            />
            {errors['client_vat'] ? (
              <span className="text-status-err">{errors['client_vat']}</span>
            ) : null}
          </label>

          <label className="md:col-span-2 flex items-center gap-2 text-sm text-ink-1">
            <input
              type="checkbox"
              checked={form.isNewClientExplicit}
              onChange={(e) => update('isNewClientExplicit', e.target.checked)}
            />
            Ny kunde uden CVR (eksplicit — fjerner CVR-advarsel)
          </label>

          <label className="md:col-span-2 flex flex-col gap-1 text-xs text-ink-2">
            Adresse (valgfri)
            <textarea
              rows={2}
              value={form.address}
              onChange={(e) => update('address', e.target.value)}
              className={`${INPUT_CLS} resize-y`}
            />
          </label>

          <label className="flex flex-col gap-1 text-xs text-ink-2">
            Salgsdato
            <input
              type="date"
              value={form.soldAt}
              onChange={(e) => update('soldAt', e.target.value)}
              className={INPUT_CLS}
            />
          </label>

          <label className="flex flex-col gap-1 text-xs text-ink-2">
            Lead-kilde
            <select
              value={form.leadSource}
              onChange={(e) => update('leadSource', e.target.value as LeadSource)}
              className={INPUT_CLS}
            >
              {(Object.keys(LEAD_SOURCE_LABEL) as LeadSource[]).map((k) => (
                <option key={k} value={k}>
                  {LEAD_SOURCE_LABEL[k]}
                </option>
              ))}
            </select>
          </label>
        </div>
      </section>

      <section className="rounded border border-line-1 bg-paper-0 p-4">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-2">Sælger</h2>
        <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            Primær sælger
            <select
              value={form.salesRepId}
              onChange={(e) => update('salesRepId', e.target.value)}
              className={INPUT_CLS}
            >
              <option value="">— vælg sælger —</option>
              {reps.map((r) => (
                <option key={r.id} value={r.id}>
                  {r.display_name}
                </option>
              ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            Ekstra sælger (valgfri)
            <select
              value={form.extraSalesRepId}
              onChange={(e) => update('extraSalesRepId', e.target.value)}
              className={INPUT_CLS}
            >
              <option value="">— ingen —</option>
              {reps
                .filter((r) => r.id !== form.salesRepId)
                .map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.display_name}
                  </option>
                ))}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-ink-2">
            Andel til ekstra sælger (%)
            <select
              value={form.extraSalesRepSharePct}
              onChange={(e) => {
                const raw = e.target.value;
                const v: '' | OrderShare =
                  raw === '25' ? 25 : raw === '50' ? 50 : raw === '75' ? 75 : '';
                update('extraSalesRepSharePct', v);
              }}
              className={INPUT_CLS}
              disabled={!form.extraSalesRepId}
            >
              <option value="">—</option>
              <option value="25">25%</option>
              <option value="50">50%</option>
              <option value="75">75%</option>
            </select>
          </label>
        </div>
      </section>

      <section className="rounded border border-line-1 bg-paper-0 p-4">
        <div className="mb-3 flex items-center justify-between">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-2">Produkter</h2>
          <button
            type="button"
            onClick={addProduct}
            className="rounded border border-line-2 px-2 py-1 text-xs text-primary"
          >
            + Tilføj produkt
          </button>
        </div>
        <div className="flex flex-col gap-3">
          {form.products.map((p, i) => {
            const productErrors: Record<string, string | undefined> = {};
            for (const [k, v] of Object.entries(errors)) {
              const prefix = `products.${i}.`;
              if (k.startsWith(prefix)) productErrors[k.slice(prefix.length)] = v;
            }
            return (
              <CreateOrderProductRow
                key={i}
                product={p}
                index={i}
                onChange={(next) => setProduct(i, next)}
                onRemove={form.products.length > 1 ? () => removeProduct(i) : undefined}
                fieldErrors={productErrors}
              />
            );
          })}
        </div>
        {errors['products'] ? (
          <p className="mt-2 text-xs text-status-err">{errors['products']}</p>
        ) : null}
      </section>

      <section className="rounded border border-line-1 bg-paper-0 p-4">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-2">
          Kontrakt (valgfri)
        </h2>
        {form.signedContractR2Key ? (
          <div className="flex flex-col gap-2">
            <p className="text-sm text-ink-0">
              <strong>{form.signedContractFilename ?? 'Kontrakt'}</strong>
            </p>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={() => contractFileRef.current?.click()}
                disabled={uploadingContract}
                className="rounded border border-line-2 px-3 py-1.5 text-sm text-primary"
              >
                {uploadingContract ? 'Erstatter…' : 'Erstat'}
              </button>
              <button
                type="button"
                onClick={clearContract}
                disabled={uploadingContract}
                className="rounded border border-line-2 px-3 py-1.5 text-sm text-ink-2"
              >
                Fjern
              </button>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <p className="text-xs text-ink-2">
              Vedhæft den underskrevne kontrakt nu — så bliver den synlig på /kunde/{'{id}'} med det
              samme. Kan også uploades senere fra ordre-redigering.
            </p>
            <div>
              <button
                type="button"
                onClick={() => contractFileRef.current?.click()}
                disabled={uploadingContract}
                className="rounded bg-primary px-3 py-1.5 text-sm text-white disabled:opacity-50"
              >
                {uploadingContract ? 'Uploader…' : 'Upload kontrakt'}
              </button>
            </div>
          </div>
        )}
        {contractUploadError ? (
          <p className="mt-2 rounded border border-status-err/40 bg-status-err/5 px-3 py-2 text-xs text-status-err">
            {contractUploadError}
          </p>
        ) : null}
        <input
          ref={contractFileRef}
          type="file"
          accept="application/pdf,image/png,image/jpeg"
          onChange={(e) => void onContractFileChosen(e)}
          className="hidden"
        />
      </section>

      <section className="rounded border border-line-1 bg-paper-0 p-4">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-ink-2">
          Ekstra email-tekst (valgfri)
        </h2>
        <textarea
          rows={3}
          value={form.emailCustomText}
          onChange={(e) => update('emailCustomText', e.target.value)}
          placeholder="Tekst der tilføjes til faktura-emails for denne ordre."
          className={`${INPUT_CLS} w-full resize-y`}
        />
      </section>

      <div className="flex justify-end gap-2">
        <button
          type="button"
          onClick={() => navigate('/orders')}
          className="rounded border border-line-2 px-3 py-1.5 text-sm text-primary"
          disabled={submitting}
        >
          Annullér
        </button>
        <button
          type="button"
          onClick={submit}
          disabled={submitting}
          className="rounded bg-primary px-4 py-1.5 text-sm text-white disabled:opacity-50"
        >
          {submitting ? 'Opretter…' : 'Registrér ordre'}
        </button>
      </div>
    </div>
  );
}

export function CreateOrderPage() {
  return (
    <Guard
      viewId="edit:admin.orders"
      fallback={
        <div className="p-6 text-sm text-ink-2">Du har ikke adgang til at oprette ordrer.</div>
      }
    >
      <CreateOrderForm />
    </Guard>
  );
}
